import { defineComponent as _defineComponent } from 'vue'

import { MetaInfo } from 'vue-meta/types/vue-meta';

const __default__ = {
	name: 'ListArticle',
	metaInfo() {
		return (this as unknown as { head: MetaInfo }).head;
	},
};

import { computed, defineAsyncComponent } from 'vue';

import PictureComp from '@/components/picture/PictureComp.vue';
import AuthorInfo from '@/features/editorial/components/AuthorInfo.vue';
import ArticleList from '@/features/editorial/components/article/list/ArticleList.vue';
import RelatedArticles from '@/features/editorial/components/article/RelatedArticles.vue';
import ArticleListStats from '@/features/editorial/components/article/list/ArticleListStats.vue';
import AdUnit from '@/components/AdUnit.vue';
import ListOffersComparison from '@/features/editorial/components/ListOffersComparison.vue';
import TrendingArticles from '@/features/editorial/components/article/TrendingArticles.vue';

import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { useI18n } from '@/helpers/composables/useI18n';

import { useEditorialArticle } from '@/features/editorial/composables/useEditorialArticle';
import { getHeaderImage } from '@/features/editorial/helpers/editorial-helper';
import { ModuleLayout } from '@/@types/graphql-types';

import type { YouTubeShortsFragment } from '@/features/editorial/graphql/fragments/YouTubeShorts.fragment';


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose }) {

const YouTubeShorts = defineAsyncComponent(
	() =>
		import(
			/* webpackChunkName: "youtube-shorts" */ '@/features/editorial/components/home/modules/blocks/YouTubeShorts.vue'
		)
);

const languageStore = useLanguageStore();
const metaStore = useMetaStore();
const { t } = useI18n();

const { article, isLoadingArticles, fetchMore } = useEditorialArticle();

const titleList = computed(() => {
	const node = article.value?.mainObjects[0]?.node;
	return node?.__typename === 'GenericTitleList' ? node : null;
});

const listSize = computed(() => titleList.value?.titles.totalCount ?? 0);

const head = computed(() => {
	if (!article.value) return { meta: [] };

	const headerImage = getHeaderImage(article.value);
	let imageUrl = headerImage.url?.startsWith('/')
		? `https://www.${JW_CONFIG.DOMAIN}${headerImage.url}`
		: headerImage.url;

	// Use .webp format replacement for link preview image compatibility
	if (imageUrl?.split('.').pop() === 'avif') imageUrl = imageUrl.replace('.avif', '.webp');

	return {
		meta: [
			// Base meta info
			{ vmid: 'og:type', property: 'og:type', content: 'article' },
			{ vmid: 'article:author', property: 'article:author', content: article.value.author.content.fullPath },
			{ vmid: 'og:locale', property: 'og:locale', content: languageStore.locale },
			{
				vmid: 'article:published_time',
				property: 'article:published_time',
				content: article.value.content.createdAt,
			},
			{
				vmid: 'article:modified_time',
				property: 'article:modified_time',
				content: article.value.content.updatedAt,
			},

			// Use header image for link previews
			{ vmid: 'og:image', property: 'og:image', content: imageUrl },
			{ vmid: 'og:image:width', property: 'og:image:width', content: '1400' },
			{ vmid: 'og:image:height', property: 'og:image:height', content: '700' },
			...(headerImage.altText
				? [{ vmid: 'og:image:alt', property: 'og:image:alt', content: headerImage.altText }]
				: []),

			// Large format twitter link preview images
			{ vmid: 'twitter:title', name: 'twitter:title', content: article.value.content.title },
			{
				vmid: 'twitter:description',
				name: 'twitter:description',
				content: metaStore.head.meta.find(meta => meta.name === 'description').content,
			},
			{ vmid: 'twitter:image', name: 'twitter:image', content: imageUrl },
			{ vmid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },

			// Extra snippets and unfurls for supported platforms
			{ vmid: 'twitter:label1', name: 'twitter:label1', content: 'Written by' },
			{ vmid: 'twitter:data1', name: 'twitter:data1', content: article.value.author.content.clearName },
		],
	};
});

const relatedVideos = computed(() => article.value?.content.relatedVideos ?? []);

const shortsBlockData = computed<YouTubeShortsFragment>(() => ({
	category: 'article_related_videos',
	layout: ModuleLayout.Carousel,
	type: 'YOUTUBE_SHORTS',
	urls: relatedVideos.value,
	translatables: { badge: '', title: t('WEBAPP_YOUTUBE_SHORTS') as string },
}));

expose({ head });

return { __sfc: true,YouTubeShorts, languageStore, metaStore, t, article, isLoadingArticles, fetchMore, titleList, listSize, head, relatedVideos, shortsBlockData, PictureComp, AuthorInfo, ArticleList, RelatedArticles, ArticleListStats, AdUnit, ListOffersComparison, TrendingArticles, getHeaderImage }
}

})